/**
 * Interiors (ภายในอาคาร) drawn: the floor of a building the player has gone into, in the same
 * realistic pixel art as the buildings outside (buildings.ts lends its materials and faces).
 *
 * A floor is painted once per camera angle (and again when the daylight or a sealed trapdoor
 * changes): the floor boards, flagstones or earth as one picture; the walls and every piece of
 * furniture as props sorted with the figures, so people walk behind tables and in front of shelves;
 * and the room's darkness as one overlay, with pools of light round its lanterns, hearth and
 * candles and daylight through its windows and door. The walls between the camera and the room are
 * cut down to a low stub showing their section, so the room stays in view (interiorPlan.ts decides
 * which). Going through a door or up the stairs, the screen fades to black and back, and the
 * floor's name comes up as it clears.
 *
 * The rules (what is where, who goes where) are all in packages/shared (interiors.ts); this only
 * reads the World's map and the floor's data.
 */
import {
  DOOR_FADE,
  getFloor,
  hash2,
  INTERIORS,
  roomFeatures,
  toScreen,
  toWorld,
  valueNoise,
  type FloorDef,
  type GameMap,
  type IsoOrigin,
  type World,
} from '@nine/shared';
import {
  M,
  Pix,
  ashlar,
  boards,
  flags,
  lattice,
  lantern,
  plaster,
  snap,
  top,
  wall,
  type Proj,
  type Pt,
  type Ramp,
  type RGB,
  type Side,
  type Tex,
} from './buildings';
import type { IsoCamera } from './camera';
import { RAMPS } from './groundArt';
import {
  cellAt,
  flightDir,
  isRoom,
  isSolidCell,
  isWall,
  lightAt,
  pieces,
  stripDepth,
  viewPlan,
  wallCut,
  type Lamp,
  type Piece,
  type Turning,
} from './interiorPlan';
import { canvas, ctx2d, selOutline } from './pixel';
import { PropGrid } from './propGrid';
import type { Prop } from './terrain';

/** Height of a standing wall (art pixels): a little over a figure, so the room reads as a room. */
const FULL = 50;
/** Height a wall between the camera and the room is cut down to. */
const CUT = 6;
/** Light on the faces: the warm key light is up and to the right. */
const LIT_L = 0.42;
const LIT_R = 0.62;
const LIT_T = 0.78;
/** Behind the building, where nothing is drawn. */
const VOID = '#0a0910';
/** The darkness of a room away from its lights: cool, as every shadow is. */
const SHADE: RGB = [9, 10, 24];

const ramp = (...hex: string[]): Ramp =>
  hex.map(
    (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)] as const,
  );

/** Ramps of the things inside, deep shadow (cool) to full light (warm). */
const R = {
  straw: ramp('#3e3220', '#5c4a2c', '#7c6638', '#9c844a', '#b8a060', '#d2bc7c'),
  indigo: ramp('#101626', '#1a2440', '#26365c', '#344a78', '#4a6492'),
  madder: ramp('#2e0e0e', '#4c1816', '#6c2620', '#8c3a2c', '#a85440'),
  ochre: ramp('#3a2810', '#5a4018', '#7c5a22', '#9c7630', '#ba9446'),
  glaze: ramp('#1a100a', '#2e1c10', '#4a2e18', '#6a4424', '#8c6034', '#b08458'),
  celadon: ramp('#1e2a24', '#33463a', '#4c6652', '#6a8a70', '#92ae94', '#bcd2b8'),
  iron: ramp('#0e0e12', '#1c1c22', '#2c2c34', '#40404a', '#5c5c66', '#80808a'),
  bronze: ramp('#2a1a0a', '#4a3014', '#6e4c20', '#94702e', '#b89448', '#d8b870'),
  ember: ramp('#5a1408', '#9a2c0c', '#d8541a', '#f08a2a', '#ffc45a', '#fff0b0'),
  water: ramp('#060a14', '#0c1424', '#142034', '#1e3048', '#2c445c', '#46647a'),
  rock: ramp('#16141a', '#24222a', '#34313a', '#47434a', '#5c575c', '#757070'),
  daylight: ramp('#8e8a7a', '#b4ae98', '#d4ccb0', '#ece4c8', '#fbf5e2'),
  linen: M.linen,
} as const;

const DYES = [R.indigo, R.madder, R.ochre] as const;

/** A picture placed in world pixels. */
interface Placed {
  readonly img: HTMLCanvasElement;
  readonly x: number;
  readonly y: number;
}

/** A light that flickers (lanterns, the hearth, candles) or holds steady (daylight), world pixels. */
interface Glow {
  readonly x: number;
  readonly y: number;
  readonly r: number;
  readonly warm: boolean;
  readonly k: number;
  readonly ph: number;
}

export interface InteriorScene {
  readonly key: string;
  readonly floor: Placed;
  readonly shade: Placed;
  readonly seal: readonly Placed[];
  readonly props: PropGrid;
  readonly glows: readonly Glow[];
  readonly dark: number;
}

const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
const bayer = (x: number, y: number): number => (BAYER[(y & 3) * 4 + (x & 3)]! + 0.5) / 16;
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

// ───────────────────────── small painting helpers ─────────────────────────

/** A box with the key light's faces: front-left (+y), front-right (+x) and top. */
function block(
  px: Pix,
  P: Proj,
  r: readonly [number, number, number, number],
  z0: number,
  z1: number,
  side: Tex,
  topTex: Tex = side,
  rightTex: Tex = side,
): void {
  const [x0, y0, x1, y1] = r;
  if (z1 > z0) {
    wall(px, P, 'left', y1, x0, x1, z0, z1, side, LIT_L);
    wall(px, P, 'right', x1, y0, y1, z0, z1, rightTex, LIT_R);
  }
  top(px, P, r, z1, topTex, LIT_T);
}

/** An upright cylinder (stool, jar, barrel, pillar): `tex` gets across (−1..1), height and light. */
function cylinder(
  px: Pix,
  P: Proj,
  cx: number,
  cy: number,
  r: number,
  z0: number,
  z1: number,
  tex: (a: number, v: number, l: number) => RGB | null,
  topTex: (a: number, b: number, l: number) => RGB | null = (a, b, l) => tex(a, z1, l + 0.2 - b * 0.1),
): void {
  const [x0, yb] = P(cx, cy, z0);
  const yt = P(cx, cy, z1)[1];
  const rx = r * 16 * Math.SQRT2;
  const ry = r * 8 * Math.SQRT2;
  for (let dx = -Math.floor(rx); dx <= Math.floor(rx); dx++) {
    const a = dx / rx;
    const e = ry * Math.sqrt(Math.max(0, 1 - a * a));
    const l = 0.46 + 0.3 * a - (a < -0.6 ? 0.1 : 0);
    for (let y = Math.round(yt + e); y <= Math.round(yb + e); y++) {
      const c = tex(a, z0 + (yb + e - y), l);
      if (c) px.set(x0 + dx, y, c);
    }
  }
  for (let dy = -Math.ceil(ry); dy <= Math.ceil(ry); dy++) {
    const b = dy / ry;
    const half = rx * Math.sqrt(Math.max(0, 1 - b * b));
    for (let dx = -Math.floor(half); dx <= Math.floor(half); dx++) {
      const c = topTex(dx / rx, b, LIT_T);
      if (c) px.set(x0 + dx, yt + dy, c);
    }
  }
}

/**
 * A round vessel (a jar) built up in slices from its foot: `shape(t)` is its radius at height
 * t (0..1) as a fraction of `r`, `tex` gets across (−1..1), height (0..1) and light.
 */
function vessel(
  px: Pix,
  P: Proj,
  cx: number,
  cy: number,
  r: number,
  h: number,
  shape: (t: number) => number,
  tex: (a: number, t: number, l: number) => RGB | null,
): void {
  for (let z = 0; z <= h; z++) {
    const t = z / h;
    const rx = r * shape(t) * 16 * Math.SQRT2;
    const ry = rx / 2;
    const [x0, y0] = P(cx, cy, z);
    for (let dy = -Math.ceil(ry); dy <= Math.ceil(ry); dy++) {
      const half = rx * Math.sqrt(Math.max(0, 1 - (dy / ry) ** 2));
      for (let dx = -Math.floor(half); dx <= Math.floor(half); dx++) {
        const a = dx / rx;
        // The top slice shows its face; below it only the rim nearest the camera is seen.
        const l = z === h ? LIT_T - dy * 0.03 : 0.46 + 0.3 * a - (a < -0.6 ? 0.1 : 0);
        const c = tex(a, t, l);
        if (c) px.set(x0 + dx, y0 + dy, c);
      }
    }
  }
}

/** A line of pixels in one colour. */
function seg(px: Pix, a: Pt, b: Pt, c: RGB, w = 1): void {
  const n = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) * 2));
  for (let i = 0; i <= n; i++) {
    const x = a[0] + ((b[0] - a[0]) * i) / n;
    const y = a[1] + ((b[1] - a[1]) * i) / n;
    for (let k = 0; k < w; k++) px.set(x + k, y, c);
  }
}

/** A canvas cut to what is drawn on it (plus a pixel), and where its corner was. */
function cropped(c: HTMLCanvasElement): { img: HTMLCanvasElement; x: number; y: number } | null {
  const d = ctx2d(c).getImageData(0, 0, c.width, c.height).data;
  let x0 = c.width,
    y0 = c.height,
    x1 = -1,
    y1 = -1;
  for (let y = 0; y < c.height; y++)
    for (let x = 0; x < c.width; x++)
      if (d[(y * c.width + x) * 4 + 3]! > 0) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
  if (x1 < 0) return null;
  const out = canvas(x1 - x0 + 1, y1 - y0 + 1);
  ctx2d(out).drawImage(c, -x0, -y0);
  return { img: out, x: x0, y: y0 };
}

// ───────────────────────── walls ─────────────────────────

/** The wall's own material on the side facing the room. */
function wallTex(look: FloorDef['wall'], h: number, i: number, j: number): Tex {
  const post = (i * 7 + j * 3) % 3 === 0;
  switch (look) {
    case 'plaster':
      return (u, v, l) => {
        if (v < 4) return snap(M.timber, l + 0.2 + (v > 3 ? 0.12 : 0), u | 0, v | 0);
        if (v > h - 5) return snap(M.timber, l + 0.05 + (v < h - 4 ? -0.12 : 0), u | 0, v | 0);
        if (post && u < 2) return snap(M.timber, l + (u < 1 ? 0.12 : 0), u | 0, v | 0);
        return plaster(0, h + 4)(u, v, l + 0.08);
      };
    case 'timber':
      return (u, v, l) => {
        if (v < 3) return snap(M.timber, l + 0.12, u | 0, v | 0);
        if (v > h - 6) return snap(M.timber, l + (v < h - 5 ? -0.15 : 0.05), u | 0, v | 0);
        return boards(M.wood, 6)(u, v, l - 0.02);
      };
    case 'stone':
      return (u, v, l) => ashlar(u, v, l - 0.06);
    case 'rock':
      return rockTex;
  }
}

/** Cave rock: broken facets and strata, darker toward its foot, a little damp moss low down. */
const rockTex: Tex = (u, v, l) => {
  const facet = valueNoise(u / 6 + 3, v / 9) - 0.5;
  const strata = Math.sin(v / 3.1 + valueNoise(u / 11, 2) * 4) > 0.82 ? -0.16 : 0;
  l += facet * 0.5 + strata + (hash2(u | 0, v | 0) - 0.5) * 0.08 - (v < 6 ? (6 - v) * 0.03 : 0);
  if (v < 5 && valueNoise(u / 4, v / 3 + 9) > 0.66) return snap(RAMPS.moss, l + 0.1, u | 0, v | 0);
  return snap(R.rock, l, u | 0, v | 0);
};

/** The same wall seen from outside the building (its back, in the dark beyond the room). */
function outsideTex(look: FloorDef['wall']): Tex {
  if (look === 'rock') return (u, v, l) => rockTex(u, v, l - 0.2);
  if (look === 'stone') return (u, v, l) => ashlar(u, v, l - 0.25);
  return (u, v, l) => snap(M.plaster, l - 0.28 + (valueNoise(u / 8, v / 12) - 0.5) * 0.1, u | 0, v | 0);
}

/** The cut top of a wall (or a standing wall's top plate), its edges lined where the wall ends. */
function sectionTex(look: FloorDef['wall'], open: { w: boolean; e: boolean; n: boolean; s: boolean }): Tex {
  const core = look === 'rock' ? R.rock : look === 'stone' ? M.stone : M.timber;
  return (u, v, l) => {
    // Lit along the edges facing the key light and the camera, dark ink along the far ones.
    if ((open.w && u < 1) || (open.n && v < 1)) return snap(core, 0.05, u | 0, v | 0, 0);
    if ((open.e && u > 15) || (open.s && v > 15)) return snap(core, l + 0.15, u | 0, v | 0, 0);
    const n =
      look === 'rock' ? (valueNoise(u / 4, v / 4) - 0.5) * 0.4 : (valueNoise(u / 5, v / 3) - 0.5) * 0.12;
    return snap(core, l - 0.3 + n, u | 0, v | 0);
  };
}

interface WallCtx {
  readonly v: readonly string[];
  readonly look: FloorDef['wall'];
  readonly day: number;
  readonly height: (i: number, j: number) => number;
}

/** Which way a wall runs (along x or y in view cells) and whether it is part of a run at all. */
const runsAlongX = (v: readonly string[], i: number, j: number): boolean =>
  isWall(cellAt(v, i - 1, j)) || isWall(cellAt(v, i + 1, j));

/** One wall cell: its faces toward the room and outside, its top, a window or the door. */
function wallSprite(c: WallCtx, i: number, j: number): { img: HTMLCanvasElement; ax: number; ay: number } {
  const { v, look, day } = c;
  const ch = cellAt(v, i, j);
  const h = c.height(i, j);
  const W = 36;
  const Hc = h + 6;
  const px = new Pix(W, Hc + 20);
  const ax = 18;
  const ay = Hc + 2;
  const P: Proj = (x, y, z) => [ax + (x - y) * 16, ay + (x + y) * 8 - z];
  const neighbour = (a: number, b: number) => (isWall(cellAt(v, a, b)) ? c.height(a, b) : 0);
  const inner = wallTex(look, h, i, j);
  const outer = outsideTex(look);
  const sideOf = (side: Side): { from: number; room: boolean; ch: string } => {
    const [a, b] = side === 'left' ? [i, j + 1] : [i + 1, j];
    const o = cellAt(v, a, b);
    return { from: neighbour(a, b), room: o !== ' ', ch: o };
  };
  const cut = wallCut(v, i, j) === 'front';
  for (const side of ['left', 'right'] as const) {
    const s = sideOf(side);
    if (s.from >= h) continue;
    const at = 1;
    const light = side === 'left' ? LIT_L : LIT_R;
    const tex = s.room ? inner : outer;
    wall(px, P, side, at, 0, 1, s.from, h, tex, light);
    if (!s.room || cut) continue;
    // A window: lattice panes with the day behind them; the door: open onto the light outside.
    if (ch === 'w') lattice(px, P, side, at, 0.2, 0.8, 16, Math.min(h - 8, 40), 0.18 + 0.6 * day);
    if (ch === 'D') doorway(px, P, side, at, Math.min(h - 6, 40), day);
  }
  if (ch === 'D' && cut) {
    // The doorway in a cut wall: the threshold stone and the stubs of its posts.
    const alongX = runsAlongX(v, i, j);
    const sill: Tex = (u, w, l) =>
      snap(M.stone, l + 0.1 + (valueNoise(u / 3, w / 3) - 0.5) * 0.15, u | 0, w | 0);
    block(px, P, [0, 0, 1, 1], 0, 2, sill);
    const postTex: Tex = (u, w, l) => snap(M.timber, l + 0.05, u | 0, w | 0);
    const ends: [number, number][] = alongX
      ? [
          [0.08, 0.5],
          [0.92, 0.5],
        ]
      : [
          [0.5, 0.08],
          [0.5, 0.92],
        ];
    for (const [x, y] of ends) block(px, P, [x - 0.08, y - 0.12, x + 0.08, y + 0.12], 0, CUT + 4, postTex);
  } else {
    const open = {
      w: !isWall(cellAt(v, i - 1, j)) || c.height(i - 1, j) < h,
      e: !isWall(cellAt(v, i + 1, j)) || c.height(i + 1, j) < h,
      n: !isWall(cellAt(v, i, j - 1)) || c.height(i, j - 1) < h,
      s: !isWall(cellAt(v, i, j + 1)) || c.height(i, j + 1) < h,
    };
    top(px, P, [0, 0, 1, 1], h, sectionTex(look, open), LIT_T);
    if (ch === 'w' && cut) {
      // A window in a cut wall: its stone sill along the section.
      const alongX = runsAlongX(v, i, j);
      const r: [number, number, number, number] = alongX
        ? [0.15, 0.35, 0.85, 0.65]
        : [0.35, 0.15, 0.65, 0.85];
      block(px, P, r, h, h + 1, (u, w, l) => snap(M.stone, l + 0.2, u | 0, w | 0));
    }
  }
  return { img: px.toCanvas(), ax, ay };
}

/** An open doorway: its frame, the door leaves swung back, the bright day outside. */
function doorway(px: Pix, P: Proj, side: Side, at: number, z1: number, day: number): void {
  wall(
    px,
    P,
    side,
    at,
    0.1,
    0.9,
    0,
    z1 + 3,
    (u, v, l) => snap(M.timber, l, u | 0, v | 0),
    side === 'left' ? LIT_L : LIT_R,
  );
  wall(px, P, side, at, 0.18, 0.82, 0, z1, (u, v) => {
    // Outside: the lit ground at the bottom, the sky glare above, both dimmed at night.
    const k = 0.25 + 0.65 * day;
    const ground = v < 8 ? -0.25 : 0;
    if (u < 4.2 || u > 12.6) return boards(M.wood, 2)(u, v, 0.35); // the leaves, swung open
    return snap(day > 0.3 ? R.daylight : R.indigo, k + ground + (v / z1) * 0.15, u | 0, v | 0);
  });
}

/** A rock wall's height: ragged, a little different cell by cell. */
const rockHeight = (seed: number, i: number, j: number, cut: boolean): number =>
  cut ? 8 + Math.floor(hash2(i * 3 + seed, j * 5) * 5) : 34 + Math.floor(hash2(i * 5, j * 7 + seed) * 14);

// ───────────────────────── furniture ─────────────────────────

interface PieceCtx {
  readonly v: readonly string[];
  readonly seed: number;
  readonly day: number;
}

/** The side of a piece a wall stands against (view directions), if any. */
function againstWall(v: readonly string[], p: Piece): 'n' | 's' | 'w' | 'e' | null {
  const wallish = (a: number, b: number) => isWall(cellAt(v, a, b));
  const count = (fn: (k: number) => boolean, n: number) =>
    Array.from({ length: n }, (_, k) => fn(k)).filter(Boolean).length;
  const sides = [
    ['n', count((k) => wallish(p.i + k, p.j - 1), p.w)],
    ['w', count((k) => wallish(p.i - 1, p.j + k), p.h)],
    ['s', count((k) => wallish(p.i + k, p.j + p.h), p.w)],
    ['e', count((k) => wallish(p.i + p.w, p.j + k), p.h)],
  ] as const;
  const best = [...sides].sort((a, b) => b[1] - a[1])[0]!;
  return best[1] > 0 ? best[0] : null;
}

const wood = (r: Ramp = M.wood, w = 4, lying = true): Tex => boards(r, w, lying);
const lacquer =
  (r: Ramp): Tex =>
  (u, v, l) =>
    snap(
      r,
      l + (valueNoise(u / 5, v / 8) - 0.5) * 0.08 + (hash2(u | 0, v | 0) > 0.985 ? 0.25 : 0),
      u | 0,
      v | 0,
    );

/** Little things on a table top at (x, y, z): a teapot and cups, a bowl, a wine jug, or nothing. */
function tableThings(px: Pix, P: Proj, x: number, y: number, z: number, h: number): void {
  const kind = Math.floor(h * 5);
  if (kind === 0) {
    // A teapot and two cups.
    cylinder(px, P, x, y, 0.1, z, z + 5, (a, _v, l) =>
      snap(R.glaze, l + 0.1 + (a > 0.3 && a < 0.6 ? 0.3 : 0), 0, 0),
    );
    const [sx, sy] = P(x + 0.12, y, z + 3);
    px.set(sx + 1, sy, R.glaze[3]!);
    px.set(sx + 2, sy - 1, R.glaze[3]!);
    for (const [cx, cy] of [
      [x - 0.12, y + 0.2],
      [x + 0.18, y + 0.16],
    ] as const)
      cylinder(px, P, cx, cy, 0.05, z, z + 2, (a, _v, l) => snap(R.celadon, l + 0.15 + a * 0.1, 0, 0));
  } else if (kind === 1) {
    // A bowl of rice, steaming a little.
    cylinder(
      px,
      P,
      x,
      y,
      0.12,
      z,
      z + 3,
      (a, _v, l) => snap(R.celadon, l + 0.05 + a * 0.15, 0, 0),
      (a, b) => (a * a + b * b < 0.55 ? snap(M.linen, 0.85 - b * 0.15, 0, 0) : snap(R.celadon, 0.7, 0, 0)),
    );
  } else if (kind === 2) {
    // A wine jug and a cup.
    cylinder(px, P, x, y, 0.08, z, z + 7, (a, v, l) =>
      v > z + 5 ? snap(R.madder, l, 0, 0) : snap(R.glaze, l + (a > 0.2 && a < 0.5 ? 0.35 : 0), 0, 0),
    );
    cylinder(px, P, x + 0.18, y + 0.1, 0.05, z, z + 2, (a, _v, l) => snap(M.linen, l + 0.2 + a * 0.1, 0, 0));
  }
}

/** One piece of furniture, painted on its own canvas (anchor: the footprint's north corner). */
function pieceSprite(c: PieceCtx, p: Piece): { img: HTMLCanvasElement; ax: number; ay: number } | null {
  const { v, seed } = c;
  const span = p.w + p.h;
  const zMax = 72;
  const W = span * 16 + 10;
  const H = span * 8 + zMax + 10;
  const px = new Pix(W, H);
  const ax = p.h * 16 + 5;
  const ay = zMax + 5;
  const P: Proj = (x, y, z) => [ax + (x - y) * 16, ay + (x + y) * 8 - z];
  const { w, h } = p;
  const hv = hash2(p.i * 13 + seed, p.j * 7 + seed * 3);
  const against = againstWall(v, p);
  const long = w >= h ? 'x' : 'y';
  switch (p.ch) {
    case 'T': {
      // A table: four legs, an apron and a thick top, with a few things on it.
      const legs: Tex = (u, z, l) => snap(M.wood, l - 0.05, u | 0, z | 0);
      for (const [x, y] of [
        [0.16, 0.16],
        [w - 0.16, 0.16],
        [0.16, h - 0.16],
        [w - 0.16, h - 0.16],
      ] as const)
        block(px, P, [x - 0.05, y - 0.05, x + 0.05, y + 0.05], 0, 10, legs);
      block(px, P, [0.1, 0.1, w - 0.1, h - 0.1], 8, 10, wood(M.wood, 32, true));
      block(
        px,
        P,
        [0.04, 0.04, w - 0.04, h - 0.04],
        10,
        12,
        wood(M.wood, 32, true),
        wood(M.deck, 5, long === 'x'),
      );
      for (let k = 0; k < Math.max(w, h); k++) {
        const [x, y] = long === 'x' ? [k + 0.5, h / 2] : [w / 2, k + 0.5];
        tableThings(px, P, x, y, 12, hash2(p.i + k * 7, p.j * 3 + seed));
      }
      break;
    }
    case 'B': {
      // A bed: a wooden frame, the mattress, a quilt in a natural dye, a pillow at the head.
      const dye = DYES[Math.floor(hv * DYES.length)]!;
      const headAtStart = long === 'x' ? against !== 'e' : against !== 's';
      block(px, P, [0.05, 0.05, w - 0.05, h - 0.05], 0, 6, wood(M.wood, 32, true));
      block(px, P, [0.1, 0.1, w - 0.1, h - 0.1], 6, 8, (u, z, l) => snap(M.linen, l, u | 0, z | 0));
      const len = long === 'x' ? w : h;
      const q0 = headAtStart ? 0.75 : 0.1;
      const q1 = headAtStart ? len - 0.1 : len - 0.75;
      const quilt: Tex = (u, z, l) => {
        const fold = Math.abs(((u / 16) % 0.5) - 0.25) < 0.02 ? -0.12 : 0;
        return snap(dye, l + fold + (valueNoise(u / 6, z / 6) - 0.5) * 0.1, u | 0, z | 0);
      };
      block(px, P, long === 'x' ? [q0, 0.08, q1, h - 0.08] : [0.08, q0, w - 0.08, q1], 6, 10, quilt);
      const pw = headAtStart ? [0.18, 0.62] : [len - 0.62, len - 0.18];
      block(
        px,
        P,
        long === 'x' ? [pw[0]!, 0.2, pw[1]!, h - 0.2] : [0.2, pw[0]!, w - 0.2, pw[1]!],
        8,
        12,
        (u, z, l) => snap(M.linen, l + 0.1, u | 0, z | 0),
      );
      const hb = headAtStart ? [0, 0.08] : [len - 0.08, len];
      block(
        px,
        P,
        long === 'x' ? [hb[0]!, 0.04, hb[1]!, h - 0.04] : [0.04, hb[0]!, w - 0.04, hb[1]!],
        0,
        18,
        wood(M.wood, 5, false),
      );
      break;
    }
    case 'C': {
      // A shop counter: panelled front, a lighter worn top, an abacus and ledger on it.
      const panel: Tex = (u, z, l) =>
        u % 16 < 1 || z < 2 || z > 14
          ? snap(M.timber, l + 0.1, u | 0, z | 0)
          : wood(M.wood, 5, false)(u, z, l);
      block(px, P, [0.05, 0.05, w - 0.05, h - 0.05], 0, 16, panel, wood(M.deck, 6, long === 'x'));
      for (let k = 0; k < Math.max(w, h); k++) {
        const [x, y] = long === 'x' ? [k + 0.5, h / 2] : [w / 2, k + 0.5];
        const t = hash2(p.i + k * 11, p.j + seed);
        if (t < 0.35) {
          block(px, P, [x - 0.22, y - 0.12, x + 0.22, y + 0.12], 16, 17, (u, z, l) =>
            snap(M.timber, l + 0.1, u | 0, z | 0),
          );
          for (let b = 0; b < 4; b++) {
            const [bx, by] = P(x - 0.15 + b * 0.1, y, 17.5);
            px.set(bx, by, R.madder[3]!);
          }
        } else if (t < 0.65)
          block(px, P, [x - 0.15, y - 0.2, x + 0.15, y + 0.2], 16, 18, (u, z, l) =>
            snap(M.paper, l + 0.25, u | 0, z | 0),
          );
        else
          cylinder(px, P, x, y, 0.1, 16, 22, (a, _z, l) =>
            snap(R.celadon, l + (a > 0.3 && a < 0.55 ? 0.3 : 0), 0, 0),
          );
      }
      break;
    }
    case 'S': {
      // Shelves against the wall: the frame, three shelves of scrolls, books and jars.
      const deep = 0.42;
      const rect: [number, number, number, number] =
        against === 'n'
          ? [0, 0, w, deep]
          : against === 's'
            ? [0, h - deep, w, h]
            : against === 'w'
              ? [0, 0, deep, h]
              : against === 'e'
                ? [w - deep, 0, w, h]
                : [0, 0.3, w, 0.3 + deep];
      const front = (side: Side): boolean =>
        side === 'left' ? against === 'n' || against === null : against === 'w';
      const shelfTex =
        (side: Side): Tex =>
        (u, z, l) => {
          if (!front(side)) return wood(M.wood, 6, false)(u, z, l - 0.08);
          if (u % 16 < 1.5 || z < 2 || z > 33 || Math.abs((z % 11) - 0.5) < 1)
            return snap(M.timber, l + 0.15, u | 0, z | 0);
          // The things on a shelf: a run of bindings, a jar, rolled scrolls.
          const slot = Math.floor(u / 4) + Math.floor(z / 11) * 7;
          const t = hash2(slot + p.i * 5, seed + p.j);
          const zz = z % 11;
          if (t < 0.3)
            return zz < 8
              ? snap(t < 0.15 ? R.indigo : R.madder, l + ((u | 0) % 2 ? 0.1 : -0.05), u | 0, z | 0)
              : snap(M.dark, 0.5, 0, 0);
          if (t < 0.5)
            return zz < 6 && Math.abs((u % 4) - 2) < 1.6
              ? snap(R.glaze, l + 0.2, u | 0, z | 0)
              : snap(M.dark, 0.6, 0, 0);
          if (t < 0.8)
            return zz < 4
              ? snap(M.paper, l + 0.3 - ((u | 0) % 2) * 0.1, u | 0, z | 0)
              : snap(M.dark, 0.6, 0, 0);
          return snap(M.dark, 0.7, 0, 0);
        };
      const [x0, y0, x1, y1] = rect;
      wall(px, P, 'left', y1, x0, x1, 0, 34, shelfTex('left'), LIT_L);
      wall(px, P, 'right', x1, y0, y1, 0, 34, shelfTex('right'), LIT_R);
      top(px, P, rect, 34, wood(M.timber, 32), LIT_T);
      break;
    }
    case 'K': {
      // The stove: a clay-plastered brick hearth, the fire showing in its mouth, a wok on top.
      const clay: Tex = (u, z, l) => {
        const mouth = Math.abs(u - 8) < 4.5 && z > 2 && z < 9;
        if (mouth)
          return z < 5
            ? snap(R.ember, 0.6 + hash2(u | 0, z | 0) * 0.4, u | 0, z | 0)
            : snap(M.dark, 0.3, 0, 0);
        return snap(M.terra, l - 0.05 + (valueNoise(u / 4, z / 5) - 0.5) * 0.18, u | 0, z | 0);
      };
      block(px, P, [0.08, 0.08, 0.92, 0.92], 0, 14, clay, (u, z, l) => snap(M.brick, l - 0.1, u | 0, z | 0));
      cylinder(
        px,
        P,
        0.5,
        0.5,
        0.3,
        14,
        16,
        (_a, _z, l) => snap(R.iron, l, 0, 0),
        (a, b) =>
          a * a + b * b > 0.72
            ? snap(R.iron, 0.75, 0, 0)
            : snap(R.iron, 0.2 + (a > 0.2 && b < -0.2 ? 0.3 : 0), 0, 0),
      );
      break;
    }
    case 'A': {
      // The altar: a red lacquer table, the ancestral tablet, two candles and a bronze censer.
      const red = lacquer(M.lacquer);
      const dim: Tex = (u, z, l) => red(u, z, l - 0.2);
      block(px, P, [0.08, 0.15, 0.92, 0.85], 0, 14, dim, (u, z, l) => red(u, z, l - 0.28));
      block(px, P, [0.38, 0.35, 0.62, 0.48], 14, 30, (u, z, l) =>
        u % 16 < 1 || z > 28 || z < 16
          ? snap(M.gold, l + 0.1, u | 0, z | 0)
          : snap(M.dark, l + 0.2, u | 0, z | 0),
      );
      for (const x of [0.2, 0.8]) {
        cylinder(px, P, x, 0.55, 0.04, 14, 21, (_a, _z, l) => snap(M.red, l + 0.2, 0, 0));
        const [fx, fy] = P(x, 0.55, 22);
        px.set(fx, fy, R.ember[4]!);
        px.set(fx, fy - 1, R.ember[5]!);
        px.set(fx, fy + 1, R.ember[3]!);
      }
      cylinder(px, P, 0.5, 0.68, 0.08, 14, 17, (a, _z, l) => snap(R.bronze, l + a * 0.15, 0, 0));
      break;
    }
    case 'W': {
      // Wine jars: round-bellied, dark glaze with a highlight, a red paper label, a cloth-tied lid.
      for (const [x, y, s] of [
        [0.32, 0.34, 1],
        [0.68, 0.64, 0.85],
      ] as const)
        vessel(
          px,
          P,
          x,
          y,
          0.3 * s,
          13 * s,
          (t) => (t > 0.86 ? 0.5 : 0.62 + 0.38 * Math.sin(Math.PI * (0.15 + t * 0.8))),
          (a, t, l) => {
            if (t > 0.86) return snap(t > 0.95 ? M.linen : R.ochre, l + 0.05, 0, 0);
            if (a > 0.05 && a < 0.4 && t > 0.35 && t < 0.62) return snap(M.red, l + 0.1, 0, 0);
            return snap(
              R.glaze,
              l - (t < 0.12 ? 0.15 : 0) + (a > 0.4 && a < 0.62 && t > 0.4 && t < 0.8 ? 0.35 : 0),
              0,
              0,
            );
          },
        );
      break;
    }
    case 'X': {
      if (hv < 0.5) {
        // A crate of rough boards with dark battens.
        const crate: Tex = (u, z, l) =>
          u % 16 < 1.5 || u % 16 > 14.5 || z < 2 || z > 12
            ? snap(M.timber, l + 0.15, u | 0, z | 0)
            : wood(M.deck, 4)(u, z, l - 0.1);
        block(px, P, [0.12, 0.12, 0.88, 0.88], 0, 14, crate);
        if (hv < 0.2) block(px, P, [0.3, 0.25, 0.7, 0.65], 14, 22, crate);
      } else {
        // A barrel: staves and iron hoops.
        cylinder(px, P, 0.5, 0.5, 0.3, 0, 16, (a, z, l) => {
          if (Math.abs(z - 3) < 1 || Math.abs(z - 13) < 1) return snap(R.iron, l + 0.1, 0, 0);
          const stave = Math.abs(((a + 1) * 5) % 1) < 0.12 ? -0.15 : 0;
          return snap(M.wood, l + stave, 0, 0);
        });
      }
      break;
    }
    case 'P': {
      // A pillar: red lacquer on a stone base, up past the walls.
      block(px, P, [0.22, 0.22, 0.78, 0.78], 0, 4, (u, z, l) => ashlar(u, z, l));
      cylinder(px, P, 0.5, 0.5, 0.2, 4, FULL + 8, (a, _z, l) =>
        snap(M.lacquer, l + (a > 0.25 && a < 0.45 ? 0.25 : 0), 0, 0),
      );
      break;
    }
    case 'L': {
      // A standing lantern: a post, a paper lantern glowing, a dark cap.
      block(px, P, [0.42, 0.42, 0.58, 0.58], 0, 24, (u, z, l) => snap(M.timber, l + 0.1, u | 0, z | 0));
      block(px, P, [0.3, 0.3, 0.7, 0.7], 0, 2, (u, z, l) => snap(M.stone, l, u | 0, z | 0));
      const paper: Tex = (u, z) =>
        u % 16 < 1.2 || z < 25 || z > 35
          ? snap(M.timber, 0.5, u | 0, z | 0)
          : snap(M.paper, 0.75 + (1 - Math.abs(((u % 16) - 8) / 8)) * 0.25, u | 0, z | 0);
      block(px, P, [0.3, 0.3, 0.7, 0.7], 24, 36, paper, (u, z) => snap(M.dark, 0.6, u | 0, z | 0));
      block(px, P, [0.26, 0.26, 0.74, 0.74], 36, 38, (u, z, l) => snap(M.timber, l, u | 0, z | 0));
      break;
    }
    case 'Z': {
      // A folding screen: three paper panels in dark frames, misty mountains in ink.
      const alongX = hv < 0.5;
      const r: [number, number, number, number] = alongX
        ? [0.05, 0.44, 0.95, 0.56]
        : [0.44, 0.05, 0.56, 0.95];
      const panel: Tex = (u, z, l) => {
        const k = (u % 16) / (16 / 3);
        if (k % 1 < 0.14 || z < 3 || z > 26) return snap(M.timber, l + 0.1, u | 0, z | 0);
        const ridge = 12 + Math.sin(u / 2.3) * 3 + Math.sin(u / 5.1 + 1) * 4;
        if (z < ridge && z > ridge - 2) return snap(M.dark, 0.9, u | 0, z | 0);
        if (z < ridge - 2) return snap(M.paper, l + 0.12, u | 0, z | 0);
        return snap(M.paper, l + 0.35, u | 0, z | 0);
      };
      block(px, P, r, 0, 28, panel, (u, z, l) => snap(M.timber, l, u | 0, z | 0));
      break;
    }
    case 'N': {
      // A rack of fishing nets: two posts, a pole, the net hung over it with cork floats.
      const alongX = long === 'x';
      const len = alongX ? w : h;
      const at = (k: number, z: number): Pt => (alongX ? P(k, 0.5, z) : P(0.5, k, z));
      for (const k of [0.12, len - 0.12]) {
        const r: [number, number, number, number] = alongX
          ? [k - 0.06, 0.44, k + 0.06, 0.56]
          : [0.44, k - 0.06, 0.56, k + 0.06];
        block(px, P, r, 0, 30, (u, z, l) => snap(M.timber, l + 0.1, u | 0, z | 0));
      }
      seg(px, at(0.1, 29), at(len - 0.1, 29), M.wood[3]!, 1);
      for (let z = 6; z < 28; z++)
        for (let s = 0.14; s < len - 0.14; s += 1 / 32) {
          const q = Math.round(s * 32);
          if ((q + z) % 3 === 0 || (q - z + 99) % 3 === 0) {
            const [x, y] = at(s, z);
            px.set(x, y, snap(M.net, 0.45 + (z / 28) * 0.4, x | 0, y | 0));
          }
        }
      for (let s = 0.3; s < len - 0.2; s += 0.35) {
        const [x, y] = at(s, 26);
        px.set(x, y, R.ochre[4]!);
        px.set(x + 1, y, R.ochre[3]!);
      }
      break;
    }
    case 'G': {
      // A chest: dark red lacquer, brass corners and lock.
      const chest: Tex = (u, z, l) => {
        if (u % 16 < 1.5 || u % 16 > 14.5) return snap(R.bronze, l + 0.2, u | 0, z | 0);
        if (Math.abs(z - 8) < 0.6) return snap(M.dark, 0.6, 0, 0);
        if (Math.abs((u % 16) - 8) < 1.5 && z > 5 && z < 8) return snap(R.bronze, l + 0.35, u | 0, z | 0);
        return lacquer(M.terra)(u, z, l);
      };
      block(px, P, [0.12, 0.22, 0.88, 0.78], 0, 11, chest, lacquer(M.terra));
      break;
    }
    case '=': {
      // The balcony rail: posts at each cell and a top rail along it.
      const alongX = long === 'x';
      const len = alongX ? w : h;
      const r = (a: number, b: number): [number, number, number, number] =>
        alongX ? [a, 0.44, b, 0.56] : [0.44, a, 0.56, b];
      for (let k = 0; k <= len; k++)
        block(px, P, r(Math.max(0, k - 0.06), Math.min(len, k + 0.06)), 0, 10, (u, z, l) =>
          snap(M.lacquer, l, u | 0, z | 0),
        );
      block(px, P, r(0, len), 8, 10, (u, z, l) => snap(M.lacquer, l + 0.1, u | 0, z | 0));
      block(px, P, r(0, len), 2, 3, (u, z, l) => snap(M.lacquer, l - 0.1, u | 0, z | 0));
      break;
    }
    case 'c': {
      // A drum stool, or a cushion on the boards.
      if (hv < 0.65)
        cylinder(px, P, 0.5, 0.5, 0.17, 0, 7, (a, z, l) =>
          Math.abs(z - 1) < 0.8 || Math.abs(z - 6) < 0.8
            ? snap(M.timber, l, 0, 0)
            : snap(M.wood, l + a * 0.1, 0, 0),
        );
      else {
        const dye = DYES[Math.floor(hv * 97) % DYES.length]!;
        block(px, P, [0.25, 0.25, 0.75, 0.75], 0, 3, (u, z, l) =>
          snap(dye, l + 0.1 + (valueNoise(u / 3, z) - 0.5) * 0.1, u | 0, z | 0),
        );
      }
      break;
    }
    case '*': {
      // A paper lantern hanging from the beams on a long cord.
      const [x, y] = P(0.5, 0.5, 64);
      const [, y1] = P(0.5, 0.5, 46);
      lantern(px, [x, y], Math.max(2, Math.round(y1 - y)));
      break;
    }
    case 'u': {
      // A ladder up through a hatch in the ceiling.
      const toward = flightDir(v, p.i, p.j);
      const alongX = toward[0] === 0;
      const rail = (o: number, z: number): Pt => (alongX ? P(0.5 + o, 0.55, z) : P(0.55, 0.5 + o, z));
      for (const o of [-0.22, 0.22]) seg(px, rail(o, 0), rail(o, 60), M.wood[2]!, 2);
      for (let z = 5; z < 60; z += 7) seg(px, rail(-0.2, z), rail(0.2, z), M.wood[4]!, 1);
      top(px, P, [0.12, 0.12, 0.88, 0.88], 62, (u, z) =>
        u < 1.5 || u > 14.5 || z < 1.5 || z > 14.5 ? snap(M.timber, 0.5, u | 0, z | 0) : M.dark[0]!,
      );
      break;
    }
    case '^': {
      // Stairs climbing away from the floor they are stepped onto from.
      const [di, dj] = flightDir(v, p.i, p.j);
      const steps: { r: [number, number, number, number]; z: number }[] = [];
      for (let k = 0; k < 4; k++) {
        const a = k / 4;
        const b = (k + 1) / 4;
        const r: [number, number, number, number] =
          di > 0
            ? [a, 0.04, b, 0.96]
            : di < 0
              ? [1 - b, 0.04, 1 - a, 0.96]
              : dj > 0
                ? [0.04, a, 0.96, b]
                : [0.04, 1 - b, 0.96, 1 - a];
        steps.push({ r, z: (k + 1) * 10 });
      }
      steps.sort((s, t) => s.r[0] + s.r[1] - (t.r[0] + t.r[1]));
      const riser: Tex = (u, z, l) =>
        z % 10 > 8.5 ? snap(M.deck, l + 0.2, u | 0, z | 0) : snap(M.wood, l - 0.05, u | 0, z | 0);
      for (const s of steps) block(px, P, s.r, 0, s.z, riser, wood(M.deck, 4, di === 0));
      break;
    }
    default:
      return null;
  }
  return { img: selOutline(px.toCanvas(), '#120d0a'), ax, ay };
}

// ───────────────────────── the floor ─────────────────────────

/** The floor's own surface at a point: boards, flags, earth or tiles (u, v in sixteenths of a tile). */
function surface(look: FloorDef['floor'], u: number, v: number, l: number, x: number, y: number): RGB {
  switch (look) {
    case 'plank': {
      const board = Math.floor(v / 4);
      const joint = (u + hash2(board, 7) * 40) % 40;
      if (v % 4 < 0.6 || joint < 0.7) return snap(M.timber, l - 0.1, x, y, 0);
      l +=
        (valueNoise(u / 7 + board * 13, v * 0.6) - 0.5) * 0.2 +
        (hash2(board, Math.floor((u + hash2(board, 7) * 40) / 40)) - 0.5) * 0.16;
      return snap(M.wood, l + 0.08, x, y);
    }
    case 'stone':
      return flags(u, v, l - 0.05) ?? M.stone[2]!;
    case 'earth': {
      l += (valueNoise(u / 6, v / 6) - 0.5) * 0.3 + (hash2(u | 0, v | 0) > 0.96 ? 0.15 : 0);
      return snap(RAMPS.dirt, l - 0.08, x, y);
    }
    case 'tile': {
      const tx = Math.floor(u / 8);
      const ty = Math.floor(v / 8);
      if (u % 8 < 0.8 || v % 8 < 0.8) return snap(M.stone, l - 0.15, x, y, 0);
      return snap(M.terra, l + 0.05 + (hash2(tx * 3, ty * 5) - 0.5) * 0.18 + (u % 8 > 6.5 ? 0.06 : 0), x, y);
    }
  }
}

interface FloorCtx {
  readonly v: readonly string[];
  readonly look: FloorDef['floor'];
  readonly t: Turning;
  readonly lamps: readonly Lamp[];
}

/** One pixel of the floor at view point (gx, gy), screen pixel (x, y) for the dither. */
function floorPixel(c: FloorCtx, gx: number, gy: number, x: number, y: number): RGB | null {
  const ci = Math.floor(gx);
  const cj = Math.floor(gy);
  const ch = cellAt(c.v, ci, cj);
  if (ch === ' ') return null;
  const fx = gx - ci;
  const fy = gy - cj;
  const [mx, my] = c.t.unturn(gx, gy);
  const u = mx * 16;
  const w = my * 16;
  // Contact shadow along the foot of walls and furniture (cool, as shadows are).
  let l = 0.56;
  for (const [di, dj, d] of [
    [-1, 0, fx],
    [1, 0, 1 - fx],
    [0, -1, fy],
    [0, 1, 1 - fy],
  ] as const) {
    const o = cellAt(c.v, ci + di, cj + dj);
    const k = isWall(o) ? 0.3 : isSolidCell(o) ? 0.16 : 0;
    if (k && d < 0.32) l -= k * (1 - d / 0.32);
  }
  // Warm light on the boards round the lamps (the overlay makes the rest dark).
  l += 0.16 * lightAt(gx, gy, 0, c.lamps);
  switch (ch) {
    case '~': {
      const ripple = Math.sin(u / 3 + valueNoise(u / 9, w / 9) * 6) > 0.85 ? 0.2 : 0;
      return snap(R.water, 0.35 + ripple + (valueNoise(u / 5, w / 5) - 0.5) * 0.2, x, y);
    }
    case 'b': {
      const board = Math.floor(w / 6);
      if (w % 6 < 1) return snap(M.tar, 0.3, x, y, 0);
      return snap(M.deck, l + 0.12 + (valueNoise(u / 8 + board * 7, w) - 0.5) * 0.2, x, y);
    }
    case ',': {
      // A rush mat with a dark cloth border.
      const edge = (a: number, b: number, d: number) => cellAt(c.v, ci + a, cj + b) !== ',' && d < 0.12;
      if (edge(-1, 0, fx) || edge(1, 0, 1 - fx) || edge(0, -1, fy) || edge(0, 1, 1 - fy))
        return snap(R.indigo, l + 0.1, x, y);
      return snap(R.straw, l + ((Math.floor(w * 1.4) % 2) * 0.08 - 0.04), x, y);
    }
    case 'v': {
      // A stairwell going down: steps falling away into the dark.
      const [di, dj] = flightDir(c.v, ci, cj);
      const t = di > 0 ? fx : di < 0 ? 1 - fx : dj > 0 ? fy : 1 - fy;
      const across = di !== 0 ? fy : fx;
      if (across < 0.07 || across > 0.93) return snap(M.timber, 0.15, x, y, 0);
      const step = Math.floor(t * 4);
      const nose = (t * 4) % 1 < 0.14;
      return snap(M.wood, 0.5 - step * 0.14 + (nose ? 0.18 : 0), x, y);
    }
    case 'h': {
      // A trapdoor: a framed hatch of boards across the floor's, an iron ring in the middle.
      if (fx < 0.1 || fx > 0.9 || fy < 0.1 || fy > 0.9) return snap(M.timber, l, x, y, 0);
      const r = Math.hypot(fx - 0.5, fy - 0.5);
      if (r > 0.08 && r < 0.13) return snap(R.iron, l + 0.2, x, y, 0);
      if (u % 5 < 0.8) return snap(M.timber, l - 0.1, x, y, 0);
      return snap(M.wood, l - 0.05 + (valueNoise(u, w / 7) - 0.5) * 0.15, x, y);
    }
  }
  if (isWall(ch)) return snap(M.dark, 0.5, x, y, 0);
  return surface(c.look, u, w, l, x, y);
}

/** Planks nailed across a trapdoor that is not to be opened yet. */
function sealSprite(): HTMLCanvasElement {
  const px = new Pix(36, 22);
  const P: Proj = (x, y, z) => [18 + (x - y) * 16, 3 + (x + y) * 8 - z];
  for (const [a, b] of [
    [
      [0.1, 0.25],
      [0.9, 0.45],
    ],
    [
      [0.15, 0.7],
      [0.85, 0.55],
    ],
  ] as const) {
    for (let k = -1; k <= 1; k++) {
      const s = P(a[0], a[1] + k * 0.03, 1);
      const e = P(b[0], b[1] + k * 0.03, 1);
      seg(px, s, e, k === -1 ? M.deck[4]! : M.deck[2]!);
    }
    const [nx, ny] = P(a[0] + 0.05, a[1], 1);
    px.set(nx, ny, R.iron[5]!);
  }
  return selOutline(px.toCanvas(), '#120d0a');
}

// ───────────────────────── light ─────────────────────────

const glowCache = new Map<string, HTMLCanvasElement>();
/** A pool of light as hard-edged dithered rings, not a smooth gradient: warm or cool daylight. */
export function glowSprite(r: number, warm: boolean): HTMLCanvasElement {
  const key = `${r}|${warm}`;
  const hit = glowCache.get(key);
  if (hit) return hit;
  const c = canvas(r * 2, r);
  const g = ctx2d(c);
  const img = g.createImageData(r * 2, r);
  const col = warm ? [255, 168, 84] : [200, 214, 236];
  for (let y = 0; y < r; y++)
    for (let x = 0; x < r * 2; x++) {
      // Squashed to lie on the floor, as the iso ground is.
      const d = Math.hypot((x + 0.5 - r) / r, (y + 0.5 - r / 2) / (r / 2));
      if (d >= 1) continue;
      const a = (1 - d) * (1 - d);
      const q = Math.floor(a * 5 + bayer(x, y)) / 5;
      if (q <= 0) continue;
      const k = (y * r * 2 + x) * 4;
      img.data[k] = col[0]!;
      img.data[k + 1] = col[1]!;
      img.data[k + 2] = col[2]!;
      img.data[k + 3] = Math.round(q * 120);
    }
  g.putImageData(img, 0, 0);
  glowCache.set(key, c);
  return c;
}

// ───────────────────────── the scene ─────────────────────────

const LAMP: Readonly<Record<string, { r: number; k: number }>> = {
  '*': { r: 3.4, k: 0.75 },
  L: { r: 3.0, k: 0.7 },
  K: { r: 3.8, k: 0.85 },
  A: { r: 2.6, k: 0.6 },
};

/**
 * Paints a floor of a building as seen at the camera's angle: `day` is how much daylight comes in
 * (0 night – 1 noon), `sealed` whether its trapdoor is still nailed shut.
 */
export function buildInteriorScene(
  map: GameMap,
  rot: number,
  origin: IsoOrigin,
  t: Turning,
  day: number,
  sealed: boolean,
  key: string,
): InteriorScene {
  const ip = map.interior!;
  const def = INTERIORS[ip.id]!;
  const fl = getFloor(ip.id, ip.floor)!;
  const n = map.w;
  const v = viewPlan(ip.plan, n, t);
  const f = roomFeatures(map);
  const seed = def.seed * 31 + ip.floor * 7 + rot;
  const inward = (x: number, y: number): [number, number] => {
    // A light in a wall (a window, the door) shines from just inside it.
    const [i, j] = [Math.floor(x), Math.floor(y)];
    for (const [di, dj] of [
      [1, 0],
      [0, 1],
      [-1, 0],
      [0, -1],
    ] as const)
      if (isRoom(cellAt(v, i + di, j + dj))) return [x + di * 0.7, y + dj * 0.7];
    return [x, y];
  };

  // The lights, in view cells.
  const lamps: (Lamp & { warm: boolean })[] = [];
  for (const L of f.lights) {
    const [x, y] = t.turn(L.x, L.y);
    const s = LAMP[L.cell] ?? { r: 3, k: 0.6 };
    lamps.push({ x, y, r: s.r, k: s.k, warm: true });
  }
  for (const w of f.windows) {
    const [x, y] = inward(...t.turn(w.x, w.y));
    lamps.push({ x, y, r: 2.8, k: 0.06 + 0.5 * day, warm: false });
  }
  for (const d of f.doors) {
    const [x, y] = inward(...t.turn(d.i + 0.5, d.j + 0.5));
    lamps.push({ x, y, r: 3.2, k: 0.08 + 0.6 * day, warm: false });
  }
  const ambient = (1 - fl.dark) * (0.6 + 0.4 * day);

  // The floor, as one picture.
  const X0 = origin.ox - n * 16;
  const Y0 = origin.oy;
  const W = n * 32;
  const H = n * 16;
  const fc = canvas(W, H);
  const fimg = ctx2d(fc).createImageData(W, H);
  const fctx: FloorCtx = { v, look: fl.floor, t, lamps };
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const [gx, gy] = toWorld(X0 + x + 0.5, Y0 + y + 0.5, origin);
      const c = floorPixel(fctx, gx, gy, x, y);
      if (!c) continue;
      const k = (y * W + x) * 4;
      fimg.data[k] = c[0];
      fimg.data[k + 1] = c[1];
      fimg.data[k + 2] = c[2];
      fimg.data[k + 3] = 255;
    }
  ctx2d(fc).putImageData(fimg, 0, 0);

  // Walls and furniture: props sorted with the figures.
  const props: Prop[] = [];
  const heights = new Map<number, number>();
  const height = (i: number, j: number): number => {
    const k = j * 4096 + i;
    let h = heights.get(k);
    if (h === undefined) {
      const ch = cellAt(v, i, j);
      const cut = wallCut(v, i, j) === 'front';
      h = ch === 'O' ? rockHeight(def.seed, i, j, cut) : cut ? (ch === 'D' ? 2 : CUT) : FULL;
      heights.set(k, h);
    }
    return h;
  };
  const wc: WallCtx = { v, look: fl.wall, day, height };
  for (let j = 0; j < n; j++)
    for (let i = 0; i < n; i++) {
      if (!isWall(cellAt(v, i, j))) continue;
      const sp = wallSprite(wc, i, j);
      const [sx, sy] = toScreen(i, j, origin);
      const cut = cropped(sp.img);
      if (cut)
        props.push({
          img: cut.img,
          x: Math.round(sx - sp.ax + cut.x),
          y: Math.round(sy - sp.ay + cut.y),
          d: i + j + 1,
        });
    }
  const pc: PieceCtx = { v, seed, day };
  for (const p of pieces(v, 'TBCSKAWXPLZNG=c*u^', 'TBCSN=')) {
    const sp = pieceSprite(pc, p);
    if (!sp) continue;
    const [sx, sy] = toScreen(p.i, p.j, origin);
    const x0 = Math.round(sx - sp.ax);
    const y0 = Math.round(sy - sp.ay);
    const c = cropped(sp.img);
    if (!c) continue;
    if (p.w + p.h <= 2) {
      // One cell: sorted at its middle (a stool a little before, so whoever stands on it is drawn over it).
      props.push({ img: c.img, x: x0 + c.x, y: y0 + c.y, d: p.i + p.j + (p.ch === 'c' ? 0.9 : 1) });
      continue;
    }
    // A long piece is cut in strips, each sorted by the front edge of the footprint under it.
    for (let sx0 = 0; sx0 < c.img.width; sx0 += 8) {
      const sw = Math.min(8, c.img.width - sx0);
      const strip = canvas(sw, c.img.height);
      ctx2d(strip).drawImage(c.img, sx0, 0, sw, c.img.height, 0, 0, sw, c.img.height);
      const u = (c.x + sx0 + sw / 2 - sp.ax) / 16;
      props.push({ img: strip, x: x0 + c.x + sx0, y: y0 + c.y, d: stripDepth(p, u) });
    }
  }

  // The trapdoor nailed shut.
  const seal: Placed[] = [];
  if (sealed) {
    const img = sealSprite();
    for (let j = 0; j < n; j++)
      for (let i = 0; i < n; i++)
        if (cellAt(v, i, j) === 'h') {
          const [sx, sy] = toScreen(i, j, origin);
          seal.push({ img, x: sx - 18, y: sy - 3 });
        }
  }

  // The darkness of the room away from its lights, as one overlay. Above the floor, a pixel is
  // lit as the floor at the foot of the wall it is on.
  const SY0 = origin.oy - FULL - 24;
  const SH = H + FULL + 28;
  const sc = canvas(W, SH);
  const simg = ctx2d(sc).createImageData(W, SH);
  for (let y = 0; y < SH; y++)
    for (let x = 0; x < W; x++) {
      let L = -1;
      for (let lift = 0; lift <= FULL + 8; lift += 3) {
        const [gx, gy] = toWorld(X0 + x + 0.5, SY0 + y + 0.5 + lift, origin);
        if (cellAt(v, Math.floor(gx), Math.floor(gy)) === ' ') continue;
        L = lightAt(gx, gy, ambient, lamps);
        break;
      }
      // Beyond the building there is nothing to darken: the void is drawn black already.
      if (L < 0) continue;
      const a = (1 - L) * 0.86;
      const q = Math.floor(a * 8 + bayer(x, y)) / 8;
      if (q <= 0) continue;
      const k = (y * W + x) * 4;
      simg.data[k] = SHADE[0];
      simg.data[k + 1] = SHADE[1];
      simg.data[k + 2] = SHADE[2];
      simg.data[k + 3] = Math.round(clamp01(q) * 255);
    }
  ctx2d(sc).putImageData(simg, 0, 0);

  // The lights that flicker (and the daylight that does not), in world pixels.
  const glows: Glow[] = lamps
    .filter((l) => l.k > 0.15)
    .map((l, k) => {
      const [sx, sy] = toScreen(l.x, l.y, origin);
      return { x: sx, y: sy, r: Math.round(l.r * 16), warm: l.warm, k: l.k, ph: k * 1.7 + seed };
    });

  return {
    key,
    floor: { img: fc, x: X0, y: Y0 },
    shade: { img: sc, x: X0, y: SY0 },
    seal,
    props: new PropGrid(props),
    glows,
    dark: fl.dark,
  };
}

// ───────────────────────── in the renderer ─────────────────────────

/** Labels for what E (or walking on) does by a door or the stairs. */
const USE_TEXT = { exit: 'ออกไปข้างนอก', up: 'ขึ้นชั้นบน', down: 'ลงชั้นล่าง' } as const;

/**
 * The interior as the renderer draws it: follows the World's map into buildings and out (turning
 * the camera onto each floor), paints each floor once, and fades the screen through each door.
 */
export class InteriorView {
  private scene: InteriorScene | null = null;
  /** How black the screen is going through a door (0 clear – 1 black). */
  private fade = 0;
  /** The floor's name, shown as the screen clears: text and seconds shown so far. */
  private label: { text: string; t: number } | null = null;

  /**
   * Keeps the camera on the World's map and the scene painted for it. Returns the scene (null
   * outside) and whether the map has just changed (the camera should jump, not glide).
   */
  sync(world: World, iso: IsoCamera, day: number): { scene: InteriorScene | null; moved: boolean } {
    const m = world.map;
    let moved = false;
    if (iso.current !== m) {
      iso.setMap(m);
      moved = true;
      const fl = m.interior ? getFloor(m.interior.id, m.interior.floor) : null;
      this.label = fl ? { text: fl.name, t: 0 } : null;
    }
    if (!m.interior) {
      this.scene = null;
      return { scene: null, moved };
    }
    const fl = getFloor(m.interior.id, m.interior.floor);
    const sealed = !!fl?.sealed && world.progress.quest.index < fl.sealed.quest;
    const key = `${m.interior.id}@${m.interior.floor}|${iso.rot}|${Math.round(day * 4)}|${sealed}`;
    if (this.scene?.key !== key)
      this.scene = buildInteriorScene(
        m,
        iso.rot,
        iso.origin,
        { turn: (x, y) => iso.turn(x, y), unturn: (x, y) => iso.unturn(x, y) },
        day,
        sealed,
        key,
      );
    return { scene: this.scene, moved };
  }

  /** The dark beyond the walls and the floor (in place of the sky and the ground). */
  drawFloor(
    g: CanvasRenderingContext2D,
    s: InteriorScene,
    ox: number,
    oy: number,
    W: number,
    H: number,
  ): void {
    g.fillStyle = VOID;
    g.fillRect(0, 0, W, H);
    g.drawImage(s.floor.img, s.floor.x + ox, s.floor.y + oy);
    for (const p of s.seal) g.drawImage(p.img, p.x + ox, p.y + oy);
  }

  /**
   * The room's darkness over everything in it, then its lights: lanterns and the hearth
   * flickering warm, daylight from the windows and door steady and cool, and in the darkest
   * rooms a little light round the player so they never lose themselves.
   */
  drawLight(
    g: CanvasRenderingContext2D,
    s: InteriorScene,
    ox: number,
    oy: number,
    time: number,
    player: readonly [number, number],
  ): void {
    g.drawImage(s.shade.img, s.shade.x + ox, s.shade.y + oy);
    g.save();
    g.globalCompositeOperation = 'lighter';
    for (const l of s.glows) {
      const flick = l.warm
        ? 0.82 + 0.1 * Math.sin(time * 7.3 + l.ph) + 0.08 * Math.sin(time * 13.1 + l.ph * 2)
        : 0.7;
      g.globalAlpha = clamp01(l.k * flick);
      const img = glowSprite(l.r, l.warm);
      g.drawImage(img, Math.round(l.x + ox - l.r), Math.round(l.y + oy - l.r / 2));
    }
    if (s.dark > 0.45) {
      g.globalAlpha = (s.dark - 0.45) * 0.9;
      const img = glowSprite(40, true);
      g.drawImage(img, Math.round(player[0] + ox - 40), Math.round(player[1] + oy - 20));
    }
    g.restore();
  }

  /** What E does near the player here (a door out, the stairs), as a label over the spot. */
  hint(world: World): { x: number; y: number; text: string } | null {
    const use = world.interiors.nearestUse();
    if (!use) return null;
    const p = world.player;
    if (use.t === 'enter') {
      const d = world.interiors.doors().find((x) => x.building === use.building);
      if (!d) return null;
      return { x: d.i + 0.5, y: d.j + 0.5, text: `เข้า${INTERIORS[d.link.interior]?.name ?? ''}` };
    }
    const f = roomFeatures(world.map);
    if (use.t === 'exit') {
      const d = [...f.doors].sort(
        (a, b) => Math.hypot(a.i - p.x, a.j - p.y) - Math.hypot(b.i - p.x, b.j - p.y),
      )[0];
      return d ? { x: d.i + 0.5, y: d.j + 0.5, text: USE_TEXT.exit } : null;
    }
    const flights = f.stairs.filter((s) => !use.dir || s.dir === use.dir);
    let best: { x: number; y: number } | null = null;
    for (const s of flights)
      for (const c of s.tiles)
        if (!best || Math.hypot(c.x - p.x, c.y - p.y) < Math.hypot(best.x - p.x, best.y - p.y)) best = c;
    return best ? { ...best, text: use.dir === -1 ? USE_TEXT.down : USE_TEXT.up } : null;
  }

  /**
   * Over the whole screen (CSS pixels): the fade through a door — out while the World holds the
   * door, back in once the scene has changed — and the floor's name as it clears.
   */
  drawScreen(
    mg: CanvasRenderingContext2D,
    busy: boolean,
    dt: number,
    w: number,
    h: number,
    font: string,
  ): void {
    this.fade = busy ? Math.min(1, this.fade + dt / DOOR_FADE) : Math.max(0, this.fade - dt / 0.45);
    if (this.fade > 0.001) {
      mg.fillStyle = `rgba(6,5,10,${this.fade.toFixed(3)})`;
      mg.fillRect(0, 0, w, h);
    }
    const lb = this.label;
    if (!lb || busy) return;
    lb.t += dt;
    const SHOW = 2.8;
    if (lb.t > SHOW) {
      this.label = null;
      return;
    }
    const a = Math.min(1, lb.t / 0.35, (SHOW - lb.t) / 0.6);
    if (a <= 0) return;
    mg.save();
    mg.globalAlpha = a;
    mg.textAlign = 'center';
    mg.font = font;
    const y = Math.round(h * 0.2);
    const tw = Math.max(160, mg.measureText(lb.text).width + 64);
    mg.fillStyle = 'rgba(12,9,14,.72)';
    mg.fillRect(Math.round(w / 2 - tw / 2), y - 26, Math.round(tw), 40);
    mg.fillStyle = 'rgba(214,176,104,.9)';
    mg.fillRect(Math.round(w / 2 - tw / 2), y - 26, Math.round(tw), 1);
    mg.fillRect(Math.round(w / 2 - tw / 2), y + 13, Math.round(tw), 1);
    mg.fillStyle = '#f2e6c8';
    mg.fillText(lb.text, Math.round(w / 2), y);
    mg.restore();
  }
}
